import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  MetricasCronService,
  MetricasEstado,
} from '../../../../auxiliares/servicios/http/metricas-cron.service';
import { GatewayService } from '../../../../auxiliares/servicios/http/gateways.service';
import { ResumenesDatosService } from '../../../../auxiliares/servicios/http/resumenes-datos.service';
import { IFilter, IQueryParam, IResumenDatos } from 'modelos/src';
import {
  DateRangeValue,
  DatePickerComponent,
} from '../../../../componentes/date-picker/date-picker.component';
import {
  GraficosComponent,
  IBarChartData,
  IHeatmapChartData,
  ILineChartData,
} from '../../../../auxiliares/graficos/graficos.component';
import { IResumenDatosBase } from 'modelos/src';
import { HelpItem, MetricaHelpComponent } from './metrica-help.component';
import { DashboardFiltrosComponent } from '../../../../componentes/dashboard-filtros/dashboard-filtros.component';
import { DashboardPanelComponent } from '../../../../componentes/dashboard-panel/dashboard-panel.component';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { LoginService } from '../../../login/login.service';
import {
  GraficoPdf,
  MatrizPdf,
  MetricasDownlinksPdfService,
} from './metricas-downlinks-pdf.service';
import {
  FilaResumen,
  ResumenPeriodoDialogComponent,
} from './resumen-periodo-dialog/resumen-periodo-dialog.component';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';

type Objetivo = 'luminaria' | 'grupo' | 'puesta' | 'grupoPuesta';

// Una fila de la tabla "Envíos por Origen". Los downlinks Manual se desglosan
// por nivel (objetivo), por eso el match puede incluir la label `objetivo`.
interface FilaOrigen {
  label: string;
  match: Record<string, string>;
}

// Un nivel de throttle en el que rebotaron jobs (métrica
// `downlinks_rate_limited_total`, label `scope`). El scope crudo viene como
// `global` | `nogw` | `gw:<eui>` y se desglosa aquí para la UI. (El scope por
// dispositivo `dev:<deveui>` se eliminó: el secuenciador serializa 1-en-vuelo
// por device, ya no hay rate-limit por deveui.)
type TipoScope = 'global' | 'gateway' | 'nogw' | 'otro';

// Una fila de la tabla de congestión por gateway. Es el plano nuevo del
// sistema: el recurso que se satura no es el dispositivo ni la fila, es el gateway
// (una sola cadena de TX y todos los ACK compitiendo en el mismo juego de
// canales), así que las métricas que importan van POR GATEWAY.
interface FilaGateway {
  gw: string;
  nombre?: string;
  cupo: number;
  // Rango del cupo en el período. Es lo que dice si el controlador ya
  // estabilizó ese gateway (min = max) o si todavía lo está moviendo.
  cupoMin: number;
  cupoMax: number;
  ocupacion: number;
  enEspera: number;
  diferidos: number;
  tasaFalloPct: number;
  ackOk: number;
  ackFalse: number;
  ackFalsePct: number;
  fugas: number;
}

interface RateLimitItem {
  tipo: TipoScope;
  icono: string;
  titulo: string;
  nombre?: string; // nombre resuelto del gateway (si se encontró)
  identificador?: string; // EUI formateado (solo gateway)
  value: number;
  pct: number; // % relativo al máximo, para la barra
}

@Component({
  selector: 'app-metricas-downlinks',
  templateUrl: './metricas-downlinks.component.html',
  styleUrl: './metricas-downlinks.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AuxiliaresModule,
    MetricaHelpComponent,
    DatePickerComponent,
    GraficosComponent,
    DashboardFiltrosComponent,
    DashboardPanelComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class MetricasDownlinksComponent implements OnInit, OnDestroy {
  private metricasService = inject(MetricasCronService);
  private gatewayService = inject(GatewayService);
  private resumenesDatosService = inject(ResumenesDatosService);
  private formularios = inject(FormularioDialogService);
  private pdf = inject(MetricasDownlinksPdfService);

  estado?: MetricasEstado;
  loading = false;
  generandoPdf = false;
  error?: string;
  ultimaActualizacion?: Date;

  // Nombres de gateway resueltos a partir de los EUI que aparecen en el rate
  // limiting. Claves en minúscula (el EUI puede venir en otra capitalización).
  private nombresGateway = new Map<string, string>();

  // Orden estable de niveles para el desglose de Manual.
  private readonly nivelesOrden: Objetivo[] = [
    'luminaria',
    'grupo',
    'puesta',
    'grupoPuesta',
  ];
  readonly nivelLabels: Record<Objetivo, string> = {
    luminaria: 'Luminaria',
    grupo: 'Grupo de luminarias',
    puesta: 'Puesta',
    grupoPuesta: 'Grupo de puestas',
  };

  private refreshInterval?: ReturnType<typeof setInterval>;

  get config() {
    return this.estado?.config;
  }

  async ngOnInit() {
    await this.refresh();
    // Auto-refresh liviano: la franja EN CURSO se lee en vivo (Redis) y se
    // mergea → tiempo real sin recargar. Solo refresca la hora actual del período.
    this.refreshInterval = setInterval((): void => {
      void this.refresh();
    }, 20_000);
  }

  ngOnDestroy() {
    clearInterval(this.refreshInterval);
  }

  async refresh() {
    this.loading = true;
    this.error = undefined;
    try {
      // Las métricas salen del período persistido; las variables de entorno
      // (config del cron) se leen del /metrics/estado (no dependen del período).
      const estadoCron = await this.metricasService.getEstado();
      const metricas = await this._metricasDesdePeriodo();
      this.estado = { metricas, config: estadoCron.config };
      await this.resolverNombresRateLimit();
      await this._cargarCoberturaNodos();
      this._construirResumenPeriodo();
      // Precomputar los agregados a campos estables (evita el loop de CD).
      this._construirAgregados();
      this.ultimaActualizacion = new Date();
    } catch (e: any) {
      this.error = e?.message || 'Error al obtener métricas de downlinks';
    } finally {
      this.loading = false;
    }
  }

  async onRangoPeriodoChange(rango: DateRangeValue): Promise<void> {
    this.rangoPeriodo = rango;
    await this.refresh();
  }

  getSample(
    metricName: string,
    labels: Record<string, string | number> = {},
  ): number {
    const m = this.estado?.metricas?.[metricName];
    if (!m?.samples?.length) return 0;
    for (const s of m.samples) {
      const match = Object.entries(labels).every(([k, v]) => s.labels[k] === v);
      if (match) return s.value;
    }
    return 0;
  }

  // Suma TODAS las samples que matchean el subconjunto de labels dado. A
  // diferencia de getSample (que devuelve la primera), agrega las que tienen
  // labels extra: `downlinks_sent_total` incluye `tipo` (tipo de dispositivo),
  // así que un mismo origen produce varias samples y hay que sumarlas para no
  // subcontar la tabla de "Envíos por Origen".
  sumSample(
    metricName: string,
    labels: Record<string, string | number> = {},
  ): number {
    const m = this.estado?.metricas?.[metricName];
    if (!m?.samples?.length) return 0;
    return m.samples
      .filter((s) =>
        Object.entries(labels).every(([k, v]) => s.labels[k] === v),
      )
      .reduce((sum, s) => sum + s.value, 0);
  }

  getTotal(metricName: string): number {
    const m = this.estado?.metricas?.[metricName];
    if (!m?.samples?.length) return 0;
    return m.samples.reduce((sum, s) => sum + s.value, 0);
  }

  getSamplesByLabel(
    metricName: string,
    labelKey: string,
  ): Array<{ label: string; value: number }> {
    const m = this.estado?.metricas?.[metricName];
    if (!m?.samples?.length) return [];
    return m.samples
      .map((s) => ({ label: String(s.labels[labelKey] ?? ''), value: s.value }))
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }

  queueSize(state: string): number {
    return this.getSample('downlinks_queue_size', { state });
  }

  // Paleta de series (dark-friendly, buen contraste sobre fondo del gráfico).
  readonly COL = {
    blue: '#3b74e8',
    teal: '#1f9d9d',
    violet: '#8257e6',
    amber: '#e0932b',
    red: '#d8483f',
    green: '#2f9e6b',
    gray: '#8b95a5',
  };

  // Paleta para gráficos de barras/columnas de UNA sola serie. app-graficos sólo
  // colorea las barras (colorByPoint) cuando recibe `colorPaleta`; sin ella deja
  // `colors: []` y las barras quedan sin relleno (invisibles). Se la pasamos a
  // embudo / latencia / convergencia / luminarias.
  readonly paletaBarras = [
    this.COL.blue,
    this.COL.teal,
    this.COL.green,
    this.COL.amber,
    this.COL.violet,
    this.COL.red,
    this.COL.gray,
  ];

  // Histograma de latencia envío→confirmación (buckets le), sobre el período
  // (los datos están en estado.metricas).
  readonly latenciaCats = ['≤15s', '≤30s', '≤60s', '≤120s', '>120s'];
  latenciaSerie(): IBarChartData[] {
    const order = ['15', '30', '60', '120', '+Inf'];
    const m = new Map(
      this.getSamplesByLabel('seq_confirm_latencia_seg', 'le').map((s) => [
        s.label,
        s.value,
      ]),
    );
    return [
      {
        name: 'Confirmaciones',
        data: order.map((o) => m.get(o) ?? 0),
        color: this.COL.violet,
      },
    ];
  }
  get hayLatencia(): boolean {
    return this.latenciaSerie()[0].data.some((v) => v > 0);
  }

  // Histograma de tiempo-a-convergencia del reconciliador (buckets le, segundos).
  // divergenteDesde → Coincide, sobre el período.
  readonly convergenciaCats = ['≤1m', '≤5m', '≤15m', '≤1h', '≤6h', '>6h'];
  convergenciaSerie(): IBarChartData[] {
    const order = ['60', '300', '900', '3600', '21600', '+Inf'];
    const m = new Map(
      this.getSamplesByLabel('reconciliation_convergencia_seg', 'le').map(
        (s) => [s.label, s.value],
      ),
    );
    return [
      {
        name: 'Configs',
        color: this.COL.green,
        data: order.map((le) => m.get(le) ?? 0),
      },
    ];
  }
  get hayConvergencia(): boolean {
    return this.convergenciaSerie()[0].data.some((v) => v > 0);
  }

  // Embudo de confirmación: dónde se cae un downlink, sobre el período.
  readonly funnelCats = [
    'Enviados',
    'ACK true',
    'Uplink ok',
    'ACK false',
    'Forzados',
  ];
  funnelSerie(): IBarChartData[] {
    return [
      {
        name: 'Downlinks',
        color: this.COL.blue,
        data: [
          this.getTotal('downlinks_sent_total'),
          this.getTotal('seq_ack_ok_total'),
          this.getTotal('seq_autoget_eco_ok_total'),
          this.getTotal('seq_ack_timeout_total'),
          this.getTotal('seq_pasos_forzados_total'),
        ],
      },
    ];
  }
  get hayFunnel(): boolean {
    return this.funnelSerie()[0].data.some((v) => v > 0);
  }

  // Luminarias afectadas por rate-limit, por limiter (métrica de cardinalidad).
  luminariasRateLimit(): IBarChartData[] {
    const s = this.getSamplesByLabel(
      'downlinks_rate_limited_luminarias',
      'scope',
    );
    return [
      {
        name: 'Luminarias',
        data: s.map((x) => x.value),
        color: this.COL.amber,
      },
    ];
  }
  luminariasRateLimitCats(): string[] {
    return this.getSamplesByLabel(
      'downlinks_rate_limited_luminarias',
      'scope',
    ).map((x) => x.label);
  }
  get hayLuminariasRl(): boolean {
    return (
      this.getSamplesByLabel('downlinks_rate_limited_luminarias', 'scope')
        .length > 0
    );
  }

  // Filas de la tabla "Envíos por Origen". Reconciliación y Auto-get van como
  // una sola fila. Manual se desglosa por nivel (objetivo): se muestran solo los
  // niveles con actividad en alguna de las 3 métricas. Si no hay ninguno (datos
  // sin label objetivo), cae a una única fila "Manual".
  filasOrigen(): FilaOrigen[] {
    const filas: FilaOrigen[] = [
      { label: 'Reconciliación', match: { origen: 'Reconciliacion' } },
    ];
    const niveles = this.nivelesManualPresentes();
    if (niveles.length === 0) {
      filas.push({ label: 'Manual', match: { origen: 'Manual' } });
    } else {
      for (const nivel of niveles) {
        filas.push({
          label: `Manual · ${this.nivelLabels[nivel]}`,
          match: { origen: 'Manual', objetivo: nivel },
        });
      }
    }
    filas.push({ label: 'Auto-get', match: { origen: 'AutoGet' } });
    filas.push({
      label: 'Consulta config inicial',
      match: { origen: 'ConsultaConfig' },
    });
    return filas;
  }

  // Niveles (objetivo) presentes en los downlinks Manual, mirando las 3 métricas
  // de origen. Devuelve en orden estable.
  private nivelesManualPresentes(): Objetivo[] {
    const metricas = [
      'downlinks_sent_total',
      'downlinks_descartados_total',
      'downlinks_failed_total',
    ];
    const presentes = new Set<string>();
    for (const name of metricas) {
      const m = this.estado?.metricas?.[name];
      for (const s of m?.samples ?? []) {
        if (s.labels['origen'] === 'Manual' && s.labels['objetivo']) {
          presentes.add(String(s.labels['objetivo']));
        }
      }
    }
    return this.nivelesOrden.filter((n) => presentes.has(n));
  }

  // ── Rate limiting ──────────────────────────────────────────────────────────
  // Desglosa la métrica `downlinks_rate_limited_total` (label `scope`) en items
  // legibles: cada uno indica EN QUÉ limiter rebotó el job (global, por gateway,
  // por dispositivo, o sin gateway). Ordenado de mayor a menor por la cuenta.
  rateLimitItems(): RateLimitItem[] {
    const samples = this.getSamplesByLabel(
      'downlinks_rate_limited_total',
      'scope',
    );
    const max = Math.max(1, ...samples.map((s) => s.value));
    return samples.map((s) => {
      const scope = s.label;
      const base = { value: s.value, pct: Math.round((s.value / max) * 100) };
      if (scope === 'global') {
        return {
          ...base,
          tipo: 'global',
          icono: 'public',
          titulo: 'Límite global',
        };
      }
      if (scope === 'nogw') {
        return {
          ...base,
          tipo: 'nogw',
          icono: 'cloud_off',
          titulo: 'Sin gateway conocido',
        };
      }
      if (scope.startsWith('gw:')) {
        const eui = scope.slice(3);
        return {
          ...base,
          tipo: 'gateway',
          icono: 'podcasts',
          titulo: 'Gateway',
          nombre: this.nombresGateway.get(eui.toLowerCase()),
          identificador: this.formatEui(eui),
        };
      }
      return { ...base, tipo: 'otro', icono: 'speed', titulo: scope };
    });
  }

  // EUI hex en mayúsculas, sin separadores.
  private formatEui(eui: string): string {
    return eui.toUpperCase();
  }

  // Trae los nombres de los gateways (por eui) que aparecen en la métrica de
  // rate limiting, en una query batch. Falla en silencio: si no se pueden
  // resolver, la UI cae al EUI crudo.
  private async resolverNombresRateLimit(): Promise<void> {
    const samples =
      this.estado?.metricas?.['downlinks_rate_limited_total']?.samples;
    if (!samples?.length) return;

    const gatewayEuis = new Set<string>();
    for (const s of samples) {
      const scope = String(s.labels['scope'] ?? '');
      if (scope.startsWith('gw:')) gatewayEuis.add(scope.slice(3));
    }

    await this.cargarNombresGateway([...gatewayEuis]);
  }

  // Devuelve cada valor en minúscula y mayúscula (sin duplicados) para cubrir
  // ambas capitalizaciones en un `$in`.
  private variantesCase(valores: string[]): string[] {
    const set = new Set<string>();
    for (const v of valores) {
      set.add(v.toLowerCase());
      set.add(v.toUpperCase());
    }
    return [...set];
  }

  private async cargarNombresGateway(euis: string[]): Promise<void> {
    const faltantes = euis.filter(
      (e) => !this.nombresGateway.has(e.toLowerCase()),
    );
    if (!faltantes.length) return;
    const variantes = this.variantesCase(faltantes);
    try {
      const res = await this.gatewayService.getFiltered({
        filter: JSON.stringify({ gatewayEui: { $in: variantes } }),
        select: 'gatewayEui nombre nombreChirpstack',
        unlimited: true,
      });
      for (const gw of res.datos ?? []) {
        const nombre = gw.nombre || gw.nombreChirpstack;
        if (gw.gatewayEui && nombre) {
          this.nombresGateway.set(gw.gatewayEui.toLowerCase(), nombre);
        }
      }
    } catch {
      /* silencioso: la UI muestra el EUI crudo */
    }
  }

  // ── Textos de ayuda por sección ──────────────────────────────────────────
  readonly helpCola: HelpItem[] = [
    {
      term: 'En espera',
      desc: 'Jobs en la fila BullMQ listos, esperando que un worker los tome.',
    },
    {
      term: 'Programados',
      desc: 'Jobs con delay futuro: auto-gets o reintentos diferidos por rate limit.',
    },
    {
      term: 'Procesando',
      desc: 'Jobs que un worker está enviando en este momento.',
    },
    {
      term: 'Completados',
      desc: 'Jobs terminados OK (enviados a Chirpstack). Se retienen hasta 7 días.',
    },
    {
      term: 'Fallidos',
      desc: 'Jobs que agotaron los 3 intentos por un error técnico de red/servidor.',
    },
    {
      term: 'Gráfico · Tamaño de fila por franja',
      desc: 'Pico de jobs en fila (en espera + programados) en cada franja horaria del período. Muestra CUÁNDO se acumuló trabajo pendiente.',
    },
    {
      term: 'Queue name',
      desc: 'Nombre de la fila BullMQ de envío. Incluye el entorno (local/dev/prod) para aislar instancias que compartan el mismo Redis.',
    },
    {
      term: 'Concurrencia',
      desc: 'Cuántos workers procesan downlinks en paralelo (downlinks a gateways distintos se envían a la vez).',
    },
  ];

  readonly helpOrigen: HelpItem[] = [
    {
      term: 'Origen',
      desc: 'Política del downlink: Reconciliación (corrector automático), Manual (acción de usuario), Auto-get (lectura encadenada tras un set) o Consulta config inicial.',
    },
    {
      term: 'Manual · nivel',
      desc: 'Los comandos manuales se desglosan por la entidad que los originó: luminaria, grupo, puesta o grupo de puestas.',
    },
    {
      term: 'Enviados',
      desc: 'Downlinks que salieron correctamente a Chirpstack.',
    },
    {
      term: 'Descartados',
      desc: 'No se enviaron porque el dispositivo estaba offline o sin sesión en el Network Server.',
    },
    {
      term: 'Fallidos',
      desc: 'Terminaron en error técnico tras agotar los reintentos de BullMQ.',
    },
    {
      term: 'Gráfico · Envíos por franja',
      desc: 'Columnas de enviados, descartados y fallidos en cada franja horaria del período (todos los orígenes sumados).',
    },
    {
      term: 'expiresAt manual / reconciliación',
      desc: 'Tiempo tras el cual Chirpstack descarta el downlink si no llegó al dispositivo. Evita que un equipo que vuelve online reciba comandos viejos. Tiempos diferenciados para comandos manuales y de reconciliación.',
    },
  ];

  readonly helpRateLimit: HelpItem[] = [
    {
      term: 'Límite global',
      desc: 'Tope de downlinks por segundo en todo el sistema. Protege al Network Server de ráfagas.',
    },
    {
      term: 'Gateway',
      desc: 'Mínimo de milisegundos entre dos downlinks al mismo gateway.',
    },
    {
      term: 'Sin gateway conocido',
      desc: 'Dispositivos cuyo último gateway aún no se registró: van a un límite conservador (1/seg) hasta que reporten.',
    },
    {
      term: 'Reprogramado',
      desc: 'El job no se descarta: se reencola con un delay + valor aleatorio (para evitar que muchos se reprogramen al mismo tiempo). Este número cuenta cuántas veces rebotó, no downlinks perdidos.',
    },
    {
      term: 'Gráfico · Rebotes por franja y limiter',
      desc: 'Mapa de calor: cuántos rebotes de rate-limit hubo en cada franja horaria (eje X) por cada limiter/scope (eje Y: global, por gateway). Más oscuro = más rebotes. Permite ver CUÁNDO y DÓNDE se satura el envío',
    },
    {
      term: 'Gráfico · Luminarias afectadas por limiter',
      desc: 'Cantidad de luminarias DISTINTAS que rebotaron en cada scope (cardinalidad, no total de rebotes). Indica qué tan extendido está el throttling. Permite ver CANTIDAD DE LUMINARIAS AFECTADAS',
    },
  ];

  readonly helpSecuenciador: HelpItem[] = [
    {
      term: 'Secuenciador',
      desc: 'Envía los downlinks de cada dispositivo de a UNO (1-en-vuelo): recién manda el siguiente cuando el anterior fue confirmado (ACK en los set, uplink de configuración en los get). Dispositivos distintos avanzan en paralelo.',
    },
    {
      term: 'Planes creados',
      desc: 'Un plan por dispositivo con downlinks pendientes en el batch.',
    },
    {
      term: 'Salteados',
      desc: 'Batches omitidos porque el dispositivo ya tenía un plan activo de igual o mayor prioridad (el cron reintenta luego).',
    },
    {
      term: 'Completados / Abortados',
      desc: 'Planes que terminaron todos sus pasos, o que se cortaron (dispositivo offline, o reemplazados por un comando de mayor prioridad).',
    },
    {
      term: 'Pasos reintentados / forzados',
      desc: 'Pasos que se reenviaron por no recibir ACK/uplink antes del deadline (timeout del paso), y los que se forzaron a avanzar tras agotar los reintentos.',
    },
    {
      term: 'Fallos al encolar',
      desc: 'Errores al meter un paso en la fila BullMQ; el timeout de respaldo del paso lo reintenta, no se pierde.',
    },
    {
      term: 'Reemplazados (deseada cambió)',
      desc: 'Planes reemplazados por otro de IGUAL prioridad porque la configuración deseada del dispositivo cambió: se corta el plan viejo (ya obsoleto) y arranca el nuevo, sin esperar a que termine.',
    },
    // ── Variables de entorno del secuenciador ──
    {
      term: 'Timeout ACK',
      desc: 'Segundos que un paso SET espera el ACK del dispositivo antes de reintentarlo o forzar el avance.',
    },
    {
      term: 'Timeout uplink',
      desc: 'Segundos que un paso GET espera los uplinks de configuración (los que confirman la lectura) antes de reintentarlo o forzar el avance.',
    },
    {
      term: 'Máx. reintentos por paso',
      desc: 'Reintentos de un mismo paso (además del envío inicial) antes de forzar el avance. Ej: 2 = 1 envío + 2 reintentos = 3 envíos.',
    },
    {
      term: 'TTL del plan',
      desc: 'Vida máxima del plan en Redis. Backstop de seguridad: si un plan quedara colgado, expira y se libera el dispositivo.',
    },
    {
      term: 'Buffer de envío',
      desc: 'Margen que se suma al timeout en el control de respaldo, que corre desde el encolado. Cubre la demora entre encolar el paso y despacharlo al Network Server (espera de rate-limit), para no reintentar antes de que el downlink haya salido.',
    },
    {
      term: 'Gráfico · Planes y pasos por franja',
      desc: 'Líneas de planes creados, completados, abortados y pasos forzados en cada franja horaria del período. Permite hacer SEGUIMIENTO de los planes.',
    },
    {
      term: 'Gráfico · Latencia SET → confirmación',
      desc: 'Histograma de cuánto tardó cada SET en confirmarse (envío → ACK/uplink), agrupado en tramos (≤15s, ≤30s, ≤60s, ≤120s, >120s). Permite DETECTAR CONGESTIONAMIENTO.',
    },
  ];

  readonly helpReconciliador: HelpItem[] = [
    {
      term: 'Reconciliador',
      desc: 'Cron que compara la configuración deseada de cada dispositivo contra su configuración real y reenvía los sets que el dispositivo no aplicó.',
    },
    { term: 'Ejecuciones', desc: 'Veces que el cron se ejecutó.' },
    {
      term: 'Divergencias',
      desc: 'Configuraciones detectadas como distintas entre lo deseado y real.',
    },
    {
      term: 'Bloqueados',
      desc: 'Dispositivos puestos en pausa tras superar los reintentos: deja de aplicar la reconciliación por unas horas.',
    },
    {
      term: 'Offline omitidos',
      desc: 'Configuraciones divergentes salteadas por estar el dispositivo sin comunicación: no se le envía el set (no llegaría) ni se le consume reintento. Reintenta cuando vuelve a comunicar, sin riesgo de quedar bloqueado durante su ventana de conexión.',
    },
    {
      term: 'Nocturna omitidos',
      desc: 'Configuraciones divergentes salteadas porque la luminaria es de energizado nocturno y está desenergizada en su ventana diurna (misma predicción que muestra el estado en el frontend): sin energía no puede recibir el set. No se envía ni se consume reintento; se aplica cuando la luminaria reenergiza de noche.',
    },
    {
      term: 'Batch size / reintentos / bloqueo',
      desc: 'Cuántos dispositivos procesa por ejecución, cuántos ciclos con diferencias tolera antes de bloquear, y por cuántas horas bloquea.',
    },
    {
      term: 'En churn',
      desc: 'Configuraciones que se re-envían ciclo tras ciclo sin llegar a coincidir (reintentos por encima del umbral). Valor de estado (foto del momento), no acumulado. Un churn alto señala configs que nunca convergen.',
    },
    {
      term: 'Gráfico · Reconciliador por franja',
      desc: 'Líneas por franja horaria de divergencias, bloqueadas, offline omitidos, nocturna omitidos y configs en churn.',
    },
    {
      term: 'Gráfico · Tiempo a convergencia',
      desc: 'Histograma de cuánto tardó cada configuración en pasar de divergente a coincidente (primera divergencia → coincide), en tramos ≤1m, ≤5m, ≤15m, ≤1h, ≤6h, >6h.',
    },
  ];

  readonly helpReintentos: HelpItem[] = [
    {
      term: 'Reintentos de descartados',
      desc: 'Cron que vuelve a encolar comandos manuales (Encender, Apagar, Pedir GPS) que se descartaron porque el dispositivo estaba offline.',
    },
    {
      term: 'Ventana de búsqueda',
      desc: 'Antigüedad máxima de los descartados que reintenta. Más viejos que eso se ignoran (ya no son relevantes).',
    },
    {
      term: 'No cubre configuración',
      desc: 'Los comandos sets descartados los recupera el reconciliador, no este cron.',
    },
    {
      term: 'Gráfico · Reencolados por franja',
      desc: 'Comandos descartados que se volvieron a encolar en cada franja horaria del período.',
    },
  ];

  readonly helpBarridoComandos: HelpItem[] = [
    {
      term: 'Barrido de comandos sin confirmar',
      desc: "Cron de respaldo que marca 'No Ejecutado' los comandos que quedaron encolados o enviados sin que el dispositivo confirmara su recepción (txack/ack) por mucho tiempo o ante caídas del sistema.",
    },
    {
      term: 'Umbral',
      desc: 'Antigüedad mínima del comando sin confirmar para barrerlo.',
    },
    {
      term: "Gráfico · Marcados 'No Ejecutado' por franja",
      desc: "Comandos marcados 'No Ejecutado' por el barrido en cada franja horaria del período.",
    },
  ];

  readonly helpConsultaConfig: HelpItem[] = [
    {
      term: 'Consulta de configuración inicial',
      desc: 'Cron que envía gets a luminarias que nunca tuvieron perfil para completar su configuración real por primera vez.',
    },
    {
      term: 'Consultados',
      desc: 'Dispositivos a los que se les envió el get inicial.',
    },
    {
      term: 'Offline omitidos',
      desc: 'Dispositivos salteados por estar sin comunicación al momento de la ejecución.',
    },
    {
      term: 'Bloqueados',
      desc: 'Dispositivos pausados tras varias consultas sin completar su configuración (circuit breaker): se dejan de consultar por unas horas.',
    },
    {
      term: 'Pendientes',
      desc: 'Dispositivos que TODAVÍA tienen la configuración real incompleta (nunca completaron el primer pull). Valor de estado (foto del momento): debería tender a bajar a medida que se completan.',
    },
    {
      term: 'Intervalo mínimo',
      desc: 'Horas que deben pasar antes de volver a consultar el mismo dispositivo (evita consultar a dispositivos que no responden).',
    },
    {
      term: 'Gráfico · Consulta config por franja',
      desc: 'Líneas por franja horaria de consultados, bloqueados, offline y pendientes.',
    },
  ];

  // ══════════════════════════════════════════════════════════════════════════
  // PERÍODO
  // Vista única: las métricas son la SUMA de las franjas horarias persistidas del
  // rango elegido (se removió el modo "en vivo"). Los gauges de estado (churn,
  // pendientes) se muestran como serie por franja (último valor de cada hora).
  // ══════════════════════════════════════════════════════════════════════════
  readonly modo = 'periodo' as const;
  hoyPeriodo = new Date();
  rangoPeriodo: DateRangeValue = this._rangoPeriodoDefault();

  // ── MOCK: poné en `true` para previsualizar el modo período sin backend. ──
  readonly USAR_MOCK_HISTORICO = false;

  readonly helpCongestion: HelpItem[] = [
    {
      term: 'Cupo',
      desc: 'Planes activos simultáneos que se le permiten a un gateway. Lo mueve el controlador con la tasa de fallo del propio gateway: +1 si va bien, −1 si va mal, a la mitad ante una tormenta. Tiene que ESTABILIZARSE; si oscila, hay que ampliar la banda muerta o alargar la ventana.',
    },
    {
      term: 'Rango del cupo en el período',
      desc: 'Mínimo y máximo que tuvo el cupo dentro del período elegido. Si coinciden, el controlador ya estabilizó ese gateway; si difieren mucho, todavía lo está moviendo y cada bajada difiere planes.',
    },
    {
      term: 'Ocupación',
      desc: 'Planes que están ocupando cupo ahora. Contra el cupo dice si el gateway está realmente lleno (hay planes esperando) o si simplemente no hay demanda.',
    },
    {
      term: 'Tasa de fallo',
      desc: 'Pasos vencidos sobre pasos resueltos de la última ventana cerrada del gateway. No cuenta los planes de dispositivos que nunca dieron señal ni los vencimientos causados por nuestra propia fila de envío: si contara esos, un nodo sordo clavaría el cupo en el piso y castigaría a los sanos.',
    },
    {
      term: 'ACK false por gateway',
      desc: 'La premisa del control de congestión: a medida que más dispositivos comparten gateway, sube el ACK false. Esta columna es la que dice si bajar el cupo lo está corrigiendo de verdad.',
    },
    {
      term: 'Esperando cupo',
      desc: 'Las dos cifras son lo mismo visto de dos maneras. "N ahora" es la foto del momento: cuántos dispositivos están en la lista de espera de ese gateway en este instante. "N veces en el período" es el acumulado: cuántas veces, a lo largo del rango elegido, un plan no pudo arrancar porque el gateway estaba lleno. Un gateway puede tener 0 esperando ahora y cientos de veces diferidas si la espera se resolvió rápido cada vez. Los diferidos salen solos cuando un plan termina y libera lugar, sin esperar el ciclo del reconciliador, y no consumen backoff de la configuración deseada.',
    },
    {
      term: 'Fugas de cupo',
      desc: 'Lugares que quedaron ocupados por planes que ya no existen (pod caído antes de cerrar, plan vencido por TTL) y se liberaron al detectarlos. Mientras están fugados, el gateway se ve lleno y difiere planes de gusto.',
    },
  ];

  readonly helpResumen: HelpItem[] = [
    {
      term: 'Resumen del período',
      desc: 'Los mismos números con los que se comparan dos corridas de perfiles, agregados por el rango de fechas elegido arriba. No hay agrupación por lote de envío: si se quieren comparar dos pruebas, se eligen los dos rangos y se miran los dos resúmenes.',
    },
    {
      term: 'Percentiles',
      desc: 'Aproximados: se derivan de los buckets del histograma (el mismo criterio con el que Prometheus calcula cuantiles sobre histogramas gruesos). Cuando el cuantil cae en el último bucket, que es abierto, se muestra un guion en vez de inventar un número.',
    },
    {
      term: 'Nodos y cobertura de puertos',
      desc: 'Salen de los resúmenes por luminaria del rango (uno por dispositivo y franja). El resumen de sistema no puede contarlos: sumar franjas contaría el mismo dispositivo muchas veces.',
    },
    {
      term: 'Deseadas al cierre',
      desc: 'Estado de todas las configuraciones deseadas al final del período, no sólo de las que se tocaron. Es el único veredicto final: el período puede cerrar con todos los pasos "OK" y la configuración igual sin quedar escrita.',
    },
  ];

  readonly helpConfirmacion: HelpItem[] = [
    {
      term: 'ACK true / ACK false',
      desc: 'De los SET enviados: cuántos dieron ACK true y cuántos ACK false en el período. ACK false alto indica problemas de bajada (gateway/RF).',
    },
    {
      term: 'Uplink ok / Uplink timeout',
      desc: 'De los auto-GET: cuántos recibieron todos los uplinks de configuración y cuántos vencieron el deadline sin recibirlos.',
    },
    {
      term: 'Gráfico · Embudo de confirmación',
      desc: 'Cascada de dónde se cae un downlink: Enviados → ACK true → Uplink ok, y a un costado ACK false y Forzados. La diferencia entre barras muestra la pérdida en cada etapa.',
    },
    {
      term: 'Gráfico · Confirmación por franja',
      desc: 'Líneas por franja horaria de ACK true, ACK false, Uplink ok y Uplink timeout. Sirve para ver CUÁNDO aparecieron los problemas.',
    },
    {
      term: 'Gráfico · ACK false ratio (%)',
      desc: 'Porcentaje de SET sin ACK por franja (ACK false / (ACK true + ACK false)). Los picos marcan los momentos con más problemas de bajada.',
    },
  ];

  // ── Gráficos temporales por franja (solo modo período; van DENTRO de cada
  //    sección, debajo de las tarjetas de totales). ──
  catFranjas: string[] = [];
  chSent: IBarChartData[] = [];
  chConfirm: ILineChartData[] = [];
  chAckRatio: ILineChartData[] = [];
  chCola: ILineChartData[] = [];
  chSeq: ILineChartData[] = [];
  chRecon: ILineChartData[] = [];
  chConsulta: ILineChartData[] = [];
  chReintentos: ILineChartData[] = [];
  chBarrido: ILineChartData[] = [];
  rlScopes: string[] = [];
  rlScopesLabels: string[] = []; // scopes con nombre de gateway resuelto (eje Y heatmap)
  chRlHeatmap: IHeatmapChartData = { data: [] };

  // ── Métricas de ESTADO (gauges) ────────────────────────────────────────────
  // Un gauge no se suma entre franjas: sumar "cupo del gateway" de 24 franjas
  // da 72 y no significa nada. Para éstas se toma el ÚLTIMO valor observado en
  // el período (la franja más reciente que lo trae).
  private readonly METRICAS_GAUGE = new Set([
    'dlk_cc_cupo',
    'dlk_cc_ocupacion',
    'dlk_cc_tasa_fallo',
    'seq_planes_en_espera',
    'seq_devices_mudos',
    'reconciliation_configs_churn',
    'reconciliation_configs_estado',
    'consulta_config_pendientes',
    'gateway_coverage_pct',
    // Derivadas del acumulador: extremos de cada franja del mismo gauge.
    'dlk_cc_cupo_min',
    'dlk_cc_cupo_max',
    'dlk_cc_ocupacion_min',
    'dlk_cc_ocupacion_max',
    'dlk_cc_tasa_fallo_min',
    'dlk_cc_tasa_fallo_max',
  ]);
  // metrica|labelsJSON → último valor del período.
  private gaugesUltimos = new Map<string, number>();

  // Mínimo / máximo de una métrica derivada `<gauge>_min` / `<gauge>_max` en
  // todo el período (cada franja aporta el suyo).
  minSample(
    metrica: string,
    labels: Record<string, string | number> = {},
  ): number {
    const vals = (this.estado?.metricas?.[metrica]?.samples ?? [])
      .filter((s) =>
        Object.entries(labels).every(([k, v]) => s.labels[k] === v),
      )
      .map((s) => s.value);
    return vals.length ? Math.min(...vals) : 0;
  }
  maxSample(
    metrica: string,
    labels: Record<string, string | number> = {},
  ): number {
    const vals = (this.estado?.metricas?.[metrica]?.samples ?? [])
      .filter((s) =>
        Object.entries(labels).every(([k, v]) => s.labels[k] === v),
      )
      .map((s) => s.value);
    return vals.length ? Math.max(...vals) : 0;
  }

  // Último valor de un gauge en el período (0 si no hubo ninguna muestra).
  gaugeUltimo(
    metrica: string,
    labels: Record<string, string | number> = {},
  ): number {
    for (const [k, v] of this.gaugesUltimos) {
      const [m, lbl] = k.split('|');
      if (m !== metrica) continue;
      const l = JSON.parse(lbl) as Record<string, string>;
      if (Object.entries(labels).every(([kk, vv]) => l[kk] === String(vv)))
        return v;
    }
    return 0;
  }

  // Agregados PRECOMPUTADOS en campos estables. Antes se bindeaban a métodos
  // (funnelSerie(), etc.) que devolvían un array NUEVO por cada ciclo de change
  // detection → Highcharts re-renderizaba → disparaba CD → loop infinito que
  // congelaba la pestaña (los charts de línea usan campos estables y por eso no
  // loopeaban). Se calculan una sola vez por refresh en _construirAgregados().
  funnelData: IBarChartData[] = [];
  hayFunnelV = false;
  latenciaData: IBarChartData[] = [];
  hayLatenciaV = false;
  convergenciaData: IBarChartData[] = [];
  hayConvergenciaV = false;
  luminariasRlData: IBarChartData[] = [];
  luminariasRlCatsV: string[] = [];
  hayLuminariasRlV = false;
  filasOrigenV: FilaOrigen[] = [];
  rateLimitItemsV: RateLimitItem[] = [];

  // Órdenes de bucket de los histogramas que el resumen del período lee como
  // percentiles (mediana y p90). Los histogramas ya no se grafican: el número
  // que sirve es el percentil, y vive en la tabla del resumen.
  private readonly esperaOrder = [
    '0.25',
    '0.5',
    '1',
    '2',
    '5',
    '15',
    '30',
    '60',
    '120',
    '+Inf',
  ];
  private readonly convOrden = [
    '60',
    '300',
    '900',
    '1800',
    '3600',
    '7200',
    '+Inf',
  ];
  private readonly gapOrden = ['5', '15', '30', '60', '120', '+Inf'];

  // ── Congestión por gateway ──
  filasGatewayV: FilaGateway[] = [];
  // Cupo y tasa de fallo como HEATMAP tiempo × gateway: con muchos gateways una
  // línea por cada uno es ilegible, y lo que se busca es el patrón (qué
  // gateway está en el piso, en qué franja).
  gwHeatCats: string[] = []; // eje Y: gateways (etiqueta HTML de 2 líneas)
  // Los mismos gateways en texto plano. El heatmap de pantalla usa etiquetas
  // HTML (nombre + EUI en dos líneas), que no sirven para el PDF.
  gwHeatNombres: string[] = [];
  chCupoHeat: IHeatmapChartData = { data: [] };
  chTasaFalloHeat: IHeatmapChartData = { data: [] };

  // ── Resumen del período ──
  filasResumenV: FilaResumen[] = [];
  // Nodos con actividad y cobertura de puertos SET: salen de los resúmenes por
  // luminaria del rango (un doc por device y franja), no de los de sistema.
  private nodosActivos = 0;
  private nodosPuertosCompletos = 0;
  private nodosPuertosOk = 0;
  private puertosSetEsperados = 0;

  private _construirAgregados(): void {
    this.funnelData = this.funnelSerie();
    this.hayFunnelV = this.funnelData[0].data.some((v) => v > 0);
    this.latenciaData = this.latenciaSerie();
    this.hayLatenciaV = this.latenciaData[0].data.some((v) => v > 0);
    this.convergenciaData = this.convergenciaSerie();
    this.hayConvergenciaV = this.convergenciaData[0].data.some((v) => v > 0);
    this.luminariasRlData = this.luminariasRateLimit();
    this.luminariasRlCatsV = this.luminariasRateLimitCats();
    this.hayLuminariasRlV = this.luminariasRlData[0].data.some((v) => v > 0);
    // Resolver nombre de gateway en las categorías de scope. Heatmap: etiqueta
    // HTML de 2 líneas (nombre + EUI chico). Barras: solo nombre.
    this.rlScopesLabels = this.rlScopes.map((s) => this.scopeLabelHtml(s));
    this.luminariasRlCatsV = this.luminariasRlCatsV.map((s) =>
      this.scopeLabel(s),
    );
    this.filasOrigenV = this.filasOrigen();
    this.rateLimitItemsV = this.rateLimitItems();
    this._construirGateways();
  }

  // Tabla por gateway: cupo vigente, ocupación, tasa de fallo de la última
  // ventana, cola de espera y ack-false propio. Los cuatro primeros son gauges
  // → último valor del período, no suma.
  private _construirGateways(): void {
    const gws = new Set<string>();
    for (const metrica of [
      'dlk_cc_cupo',
      'dlk_cc_ocupacion',
      'dlk_cc_tasa_fallo',
      'seq_planes_en_espera',
      'seq_planes_diferidos_total',
      'seq_ack_por_gateway_total',
      'seq_ocupacion_fugada_total',
    ]) {
      for (const s of this.estado?.metricas?.[metrica]?.samples ?? []) {
        const gw = String(s.labels['gw'] ?? '');
        if (gw) gws.add(gw);
      }
    }
    this.filasGatewayV = [...gws]
      .map((gw) => {
        const ackOk = this.sumSample('seq_ack_por_gateway_total', {
          gw,
          resultado: 'ok',
        });
        const ackFalse = this.sumSample('seq_ack_por_gateway_total', {
          gw,
          resultado: 'false',
        });
        const t = ackOk + ackFalse;
        return {
          gw,
          nombre: this.nombresGateway.get(gw.toLowerCase()),
          cupo: this.gaugeUltimo('dlk_cc_cupo', { gw }),
          // `_min` / `_max` son las métricas derivadas que publica el
          // acumulador para cada gauge: los extremos de cada franja, no el
          // último valor (un cupo que osciló dentro de la hora se vería plano).
          cupoMin: this.minSample('dlk_cc_cupo_min', { gw }),
          cupoMax: this.maxSample('dlk_cc_cupo_max', { gw }),
          ocupacion: this.gaugeUltimo('dlk_cc_ocupacion', { gw }),
          enEspera: this.gaugeUltimo('seq_planes_en_espera', { gw }),
          diferidos: this.sumSample('seq_planes_diferidos_total', { gw }),
          // La tasa se persiste como fracción 0..1 (la escribe el controlador).
          tasaFalloPct: Math.round(
            this.gaugeUltimo('dlk_cc_tasa_fallo', { gw }) * 100,
          ),
          ackOk,
          ackFalse,
          ackFalsePct: t ? Math.round((ackFalse / t) * 100) : 0,
          fugas: this.sumSample('seq_ocupacion_fugada_total', { gw }),
        };
      })
      .sort((a, b) => b.ackFalsePct - a.ackFalsePct);
  }

  // ── Formateo ──────────────────────────────────────────────────────────────
  // Segundos → la unidad en la que el número se lee: los tiempos de este
  // sistema van de 0,1 s (despacho sano) a 60 min (convergencia mala).
  fmtSeg(seg?: number): string {
    if (seg == null) return '';
    if (seg < 1) return `${seg.toFixed(2).replace('.', ',')} s`;
    if (seg < 90) return `${Math.round(seg)} s`;
    return `${(seg / 60).toFixed(1).replace('.', ',')} min`;
  }

  private fmtNum(n?: number): string {
    return n == null ? '' : n.toLocaleString('es-AR');
  }

  private fmtPct(parte?: number, total?: number): string {
    if (!parte && !total) return '0';
    if (!total) return this.fmtNum(parte);
    return `${this.fmtNum(parte ?? 0)} (${Math.round(((parte ?? 0) / total) * 100)} %)`;
  }

  // Etiqueta legible de un scope de rate-limit: si es un gateway (`gw:<eui>`) y
  // hay nombre resuelto, muestra el nombre; si no, el EUI formateado.
  scopeLabel(scope: string): string {
    if (scope === 'global') return 'Global';
    if (scope === 'nogw') return 'Sin gateway';
    if (scope.startsWith('gw:')) {
      const eui = scope.slice(3);
      return this.nombresGateway.get(eui.toLowerCase()) ?? this.formatEui(eui);
    }
    return scope;
  }

  // Igual que scopeLabel pero para el eje del heatmap (useHTML): cuando hay
  // nombre de gateway, muestra el nombre y DEBAJO el EUI en chico.
  scopeLabelHtml(scope: string): string {
    if (scope.startsWith('gw:')) {
      const eui = scope.slice(3);
      const nombre = this.nombresGateway.get(eui.toLowerCase());
      const euiFmt = this.formatEui(eui);
      if (nombre) {
        return `<div style="line-height:1.1;text-align:right">${escaparHtml(nombre)}<br><span style="font-size:9px;opacity:.55;font-family:monospace">${euiFmt}</span></div>`;
      }
      return euiFmt;
    }
    return this.scopeLabel(scope);
  }

  get hayCharts(): boolean {
    return this.modo === 'periodo' && this.catFranjas.length > 0;
  }

  // Si alguna serie tiene un valor distinto de cero. Sin valores el gráfico
  // quedaría con los ejes vacíos: se muestra un aviso en su lugar.
  conValores(series: ILineChartData[] | undefined): boolean {
    return (series ?? []).some((serie) =>
      (serie.data ?? []).some((punto) =>
        Array.isArray(punto) ? !!punto[1] : !!punto,
      ),
    );
  }

  // Reconstruye `estado.metricas` para el período sumando los samples persistidos.
  private async _metricasDesdePeriodo(): Promise<MetricasEstado['metricas']> {
    const datos = await this._fetchDatosPeriodo();
    this._construirCharts(datos);

    const acc = new Map<
      string,
      { metrica: string; labels: Record<string, string>; value: number }
    >();
    // Gauges: se recorre en orden cronológico y gana la ÚLTIMA franja. Sumarlos
    // entre franjas no significa nada (24 franjas × cupo 3 = 72).
    //
    // Pero hay una franja POR CLIENTE por hora: dentro de la franja más reciente
    // se suman los clientes. Quedarse con la última muestra leída daba el valor
    // de un solo cliente ("1 coinciden" con 6.650 deseadas). Los gauges por
    // gateway se guardan sin cliente, así que ahí hay una sola muestra por
    // franja y la suma no los altera.
    this.gaugesUltimos.clear();
    const ultimas = new Map<string, { periodo: string; valor: number }>();
    const cronologico = [...datos].sort((a, b) =>
      (a.periodoInicio ?? '') < (b.periodoInicio ?? '') ? -1 : 1,
    );
    for (const d of cronologico) {
      const periodo = d.periodoInicio ?? '';
      for (const sa of d.resumen?.samples ?? []) {
        if (!this.METRICAS_GAUGE.has(sa.metrica)) continue;
        const key = sa.metrica + '|' + JSON.stringify(sa.labels ?? {});
        const previa = ultimas.get(key);
        if (previa?.periodo === periodo) previa.valor += sa.valor ?? 0;
        else ultimas.set(key, { periodo, valor: sa.valor ?? 0 });
      }
    }
    for (const [key, u] of ultimas) this.gaugesUltimos.set(key, u.valor);
    let colaMax = 0;
    let colaPromSum = 0;
    let colaPromN = 0;
    for (const d of datos) {
      const r = d.resumen;
      if (!r) continue;
      for (const s of r.samples ?? []) {
        const labels = s.labels ?? {};
        // Los gauges (y sus derivadas _min/_max) NO se suman entre franjas: se
        // guardan como samples por franja y se resuelven con
        // gaugeUltimo/minSample/maxSample. Sumar "cupo del gateway" de 24
        // franjas da 72 y no significa nada.
        const key =
          s.metrica +
          '|' +
          JSON.stringify(labels) +
          (this.METRICAS_GAUGE.has(s.metrica) ? `|${d.periodoInicio}` : '');
        const e = acc.get(key) ?? { metrica: s.metrica, labels, value: 0 };
        e.value += s.valor ?? 0;
        acc.set(key, e);
      }
      if (r.colaProfundidadMax != null)
        colaMax = Math.max(colaMax, r.colaProfundidadMax);
      if (r.colaProfundidadProm != null) {
        colaPromSum += r.colaProfundidadProm;
        colaPromN++;
      }
    }
    const metricas: MetricasEstado['metricas'] = {};
    for (const e of acc.values()) {
      const m = (metricas[e.metrica] ??= {
        type: 'counter',
        help: '',
        samples: [],
      });
      m.samples.push({ labels: e.labels, value: e.value });
    }
    // Cola (gauge): en período mostramos el pico y el promedio de las franjas.
    metricas['downlinks_queue_size'] = {
      type: 'gauge',
      help: '',
      samples: [
        { labels: { state: 'waiting' }, value: colaMax },
        {
          labels: { state: 'delayed' },
          value: colaPromN ? Math.round(colaPromSum / colaPromN) : 0,
        },
      ],
    };
    return metricas;
  }

  // ── RESUMEN DEL PERÍODO ───────────────────────────────────────────────────
  // Mismos números con los que se comparan dos corridas de perfiles, pero
  // agregados por el MISMO filtro temporal que el resto del dashboard: no hay
  // ninguna noción de "tanda" ni agrupación por lote de envío. Si se quiere
  // comparar dos pruebas, se eligen los dos rangos.
  //
  // Todo sale de las franjas horarias persistidas: los contadores se suman y
  // los percentiles se derivan de los buckets (mismo criterio que Prometheus
  // sobre un histograma grueso).

  // Nodos con actividad y cobertura de puertos SET del rango. Estos dos salen
  // de los resúmenes POR LUMINARIA (uno por device y franja): el resumen de
  // sistema no puede saber a cuántos dispositivos distintos tocó, porque sumar
  // franjas contaría el mismo device varias veces.
  private async _cargarCoberturaNodos(): Promise<void> {
    this.nodosActivos = 0;
    this.nodosPuertosCompletos = 0;
    this.nodosPuertosOk = 0;
    this.puertosSetEsperados = 0;
    if (!this.rangoPeriodo?.start || !this.rangoPeriodo?.end) return;
    try {
      const filter: IFilter<IResumenDatos> = {
        tipo: 'Downlinks Luminaria',
        periodoInicio: {
          $gte: this.rangoPeriodo.start,
          $lte: this.rangoPeriodo.end,
        },
      } as unknown as IFilter<IResumenDatos>;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        includeChildren: true,
        // Sólo lo necesario: son un doc por device y franja.
        select: 'idsAsignados resumen',
        limit: 100000,
      };
      const res =
        await this.resumenesDatosService.getFiltered<'Downlinks Luminaria'>(
          query,
        );
      // Por device gana el plan más completo del rango (los campos de plan no
      // se acumulan: son del último plan que cerró en esa franja).
      const porDevice = new Map<string, { set: number; ok: number }>();
      for (const d of res.datos ?? []) {
        const deveui = d.idsAsignados?.[0];
        if (!deveui) continue;
        const r = d.resumen;
        const prev = porDevice.get(deveui) ?? { set: 0, ok: 0 };
        porDevice.set(deveui, {
          set: Math.max(prev.set, r?.puertosSet ?? 0),
          ok: Math.max(prev.ok, r?.puertosSetOk ?? 0),
        });
      }
      this.nodosActivos = porDevice.size;
      // El plan más largo del rango define el 100 % de cobertura.
      this.puertosSetEsperados = Math.max(
        0,
        ...[...porDevice.values()].map((x) => x.set),
      );
      for (const x of porDevice.values()) {
        if (this.puertosSetEsperados && x.set >= this.puertosSetEsperados)
          this.nodosPuertosCompletos++;
        if (this.puertosSetEsperados && x.ok >= this.puertosSetEsperados)
          this.nodosPuertosOk++;
      }
    } catch {
      /* silencioso: las filas de cobertura quedan en 0 */
    }
  }

  // Percentil aproximado sobre un histograma de buckets (label `le`). Devuelve
  // el borde del primer bucket cuyo acumulado alcanza el cuantil; si cae en el
  // bucket abierto, devuelve undefined (no se inventa un número).
  private percentilBuckets(
    metrica: string,
    orden: string[],
    q: number,
  ): number | undefined {
    const m = new Map(
      this.getSamplesByLabel(metrica, 'le').map((x) => [x.label, x.value]),
    );
    const total = orden.reduce((acc, le) => acc + (m.get(le) ?? 0), 0);
    if (!total) return undefined;
    let acum = 0;
    for (const le of orden) {
      acum += m.get(le) ?? 0;
      if (acum >= q * total) return le === '+Inf' ? undefined : Number(le);
    }
    return undefined;
  }

  private _construirResumenPeriodo(): void {
    const pasos = this.getTotal('seq_pasos_veredicto_total');
    const envios = this.getTotal('downlinks_sent_total');
    const ack = this.sumSample('seq_pasos_veredicto_total', { via: 'ack' });
    const ackNoAplicado = this.sumSample('seq_pasos_veredicto_total', {
      via: 'ack-no-aplicado',
    });
    // Rechazo del device (FFxx / ERROR_DL): cierra el paso, pero NO es fast-path
    // ni cierre favorable — el device descartó la trama explícitamente.
    const rechazado = this.sumSample('seq_pasos_veredicto_total', {
      via: 'rechazado',
    });
    const eco = this.sumSample('seq_pasos_veredicto_total', { via: 'eco' });
    const timeout = this.sumSample('seq_pasos_veredicto_total', {
      via: 'timeout',
    });
    const fastPath = ack + ackNoAplicado;
    const cerrados = fastPath + eco;
    const agotados = this.sumSample('seq_envios_por_paso', { envios: '3+' });

    const convP50 = this.percentilBuckets(
      'seq_convergencia_plan_seg',
      this.convOrden,
      0.5,
    );
    const convP90 = this.percentilBuckets(
      'seq_convergencia_plan_seg',
      this.convOrden,
      0.9,
    );
    const espP50 = this.percentilBuckets(
      'downlinks_espera_cola_seg',
      this.esperaOrder,
      0.5,
    );
    const espP90 = this.percentilBuckets(
      'downlinks_espera_cola_seg',
      this.esperaOrder,
      0.9,
    );
    const gapP50 = this.percentilBuckets(
      'seq_gap_envios_seg',
      this.gapOrden,
      0.5,
    );

    const fila = (
      label: string,
      valor: string,
      extra?: { destacado?: boolean; ayuda?: string },
    ): FilaResumen => ({ label, valor, ...extra });

    this.filasResumenV = [
      fila('Nodos con actividad', this.fmtNum(this.nodosActivos)),
      fila(
        'Planes (creados / completados / abortados)',
        `${this.getTotal('seq_planes_creados_total')} / ${this.getTotal(
          'seq_planes_completados_total',
        )} / ${this.getTotal('seq_planes_abortados_total')}`,
      ),
      fila(
        'Veces que un plan esperó cupo de gateway',
        this.fmtNum(this.getTotal('seq_planes_diferidos_total')),
        {
          ayuda:
            'Acumulado del período: cuántas veces un plan no pudo arrancar ' +
            'porque su gateway estaba lleno y quedó en la lista de espera. No ' +
            'es "cuántos están esperando ahora" (eso es la foto de la tabla de ' +
            'gateways) y no consume backoff de la configuración deseada.',
        },
      ),
      fila('Pasos resueltos', this.fmtNum(pasos)),
      fila('Downlinks emitidos', this.fmtNum(envios)),
      fila(
        'Envíos por paso',
        pasos ? (envios / pasos).toFixed(2).replace('.', ',') : '',
        {
          destacado: true,
          ayuda:
            '1,00 es el piso teórico: un envío por paso, confirmado al primer ' +
            'intento.',
        },
      ),
      fila('Pasos que agotaron los envíos', this.fmtPct(agotados, pasos), {
        destacado: true,
      }),
      fila('Convergencia del nodo (mediana)', this.fmtSeg(convP50), {
        destacado: true,
        ayuda:
          'Primer envío del plan → cierre del plan, por nodo. Percentiles ' +
          'aproximados: se derivan de los buckets del histograma.',
      }),
      fila('idem p90', this.fmtSeg(convP90)),
      fila(
        'Nodos que recorrieron todos los puertos SET',
        this.puertosSetEsperados
          ? `${this.nodosPuertosCompletos} / ${this.nodosActivos} (${this.puertosSetEsperados} puertos)`
          : '',
      ),
      fila(
        'Nodos con todos los puertos SET en veredicto favorable',
        this.puertosSetEsperados
          ? `${this.nodosPuertosOk} / ${this.nodosActivos}`
          : '',
        { destacado: true },
      ),
      fila('Espera en la fila de envío (mediana)', this.fmtSeg(espP50), {
        ayuda:
          'Puesto en la fila → downlink emitido. Es latencia NUESTRA: si ' +
          'supera el deadline del paso, el ACK del dispositivo no podía llegar ' +
          'en plazo.',
      }),
      fila('idem p90', this.fmtSeg(espP90)),
      fila('Gap entre envíos de un mismo paso (mediana)', this.fmtSeg(gapP50)),
      fila('Cierres por fast-path del ACK', this.fmtPct(fastPath, cerrados), {
        ayuda:
          'El ACK confirma que la trama llegó, no que la configuración quedó ' +
          'escrita. Un fast-path alto significa que buena parte del período se ' +
          'dio por bueno sin verificar.',
      }),
      fila(
        'Cierres por uplink real del dispositivo (eco verificado)',
        this.fmtPct(eco, cerrados),
      ),
      fila(
        'Auto-GET salteados',
        this.fmtNum(this.getTotal('seq_autoget_salteados_total')),
      ),
      fila('Pasos forzados por timeout', this.fmtNum(timeout)),
      fila('Pasos rechazados por el dispositivo', this.fmtNum(rechazado), {
        ayuda:
          'El device recibió la trama y la descartó (FFxx en ACTIS, ERROR_DL en ' +
          'GPE). NO es pérdida de radio: el enlace funcionó. Suele indicar un ' +
          'payload que el firmware no acepta.',
      }),
      fila(
        'Huérfanos (emitidos tras el veredicto del paso)',
        this.fmtPct(this.getTotal('downlinks_huerfanos_total'), envios),
        {
          destacado: true,
          ayuda:
            'El envío anterior ya había cerrado el paso mientras éste esperaba ' +
            'en la fila: aire gastado y comandos que nadie confirma.',
        },
      ),
      fila(
        'Obsoletos (matados antes de salir)',
        this.fmtNum(this.getTotal('downlinks_obsoletos_total')),
      ),
      fila(
        'Planes cortados por el breaker (sin señal)',
        this.fmtNum(
          this.getSample('seq_planes_abortados_total', { motivo: 'sin_senal' }),
        ),
      ),
      fila(
        'Rebotes de rate limit',
        this.fmtNum(this.getTotal('downlinks_rate_limited_total')),
      ),
      // Veredicto del COMANDO (lo sella gestion-lora-luminarias). Es la verdad
      // del dispositivo y no coincide con el veredicto del paso: un paso puede
      // cerrar por fast-path y su comando terminar en 'No Recibido'.
      fila(
        'Comandos No Recibido (ACK false)',
        this.fmtNum(
          this.sumSample('comandos_estado_final_total', {
            estado: 'No Recibido',
          }),
        ),
      ),
      fila(
        'Comandos No Ejecutado (el uplink desmiente)',
        this.fmtNum(
          this.sumSample('comandos_estado_final_total', {
            estado: 'No Ejecutado',
          }),
        ),
      ),
      fila(
        'Rechazos del dispositivo (FFxx)',
        this.fmtNum(this.getTotal('uplink_rechazo_ffxx_total')),
        {
          ayuda:
            'La trama llegó y el equipo la descartó (por ejemplo FF02, largo ' +
            'de payload incorrecto). No es pérdida de radio: reenviar lo mismo ' +
            'no cambia nada.',
        },
      ),
      fila(
        'Contradicciones',
        this.fmtNum(this.getTotal('comandos_contradiccion_total')),
        {
          destacado: true,
          ayuda:
            'Comandos que quedaron en un estado que contradice su propio sello ' +
            '(dados por aplicados por ACK y después degradados por un ACK ' +
            'false). Si aparecen, el diagnóstico del período es sospechoso.',
        },
      ),
      fila(
        'Dispositivos mudos al cierre',
        this.fmtNum(this.gaugeUltimo('seq_devices_mudos')),
        {
          ayuda:
            'Tienen la marca vigente porque su último plan completo no obtuvo ' +
            'ni una señal: a esos les puede cortar el plan el circuit breaker ' +
            'en la próxima tanda.',
        },
      ),
      fila(
        'Colas purgadas en el network server',
        this.fmtNum(this.getTotal('seq_flush_queue_total')),
        {
          ayuda:
            'Al desistir de un plan hay que borrar lo que ya se encoló en ' +
            'Chirpstack: los items de un dispositivo inalcanzable no expiran ' +
            'solos.',
        },
      ),
      fila(
        'Deseadas al cierre (Coincide / No coincide / Pendiente)',
        `${this.gaugeUltimo('reconciliation_configs_estado', {
          estado: 'Coincide',
        })} / ${this.gaugeUltimo('reconciliation_configs_estado', {
          estado: 'No coincide',
        })} / ${this.gaugeUltimo('reconciliation_configs_estado', {
          estado: 'Pendiente',
        })}`,
        {
          destacado: true,
          ayuda:
            'Estado de todas las deseadas al final del período: es el único ' +
            'veredicto final (un paso puede cerrar OK y la configuración igual ' +
            'no quedar escrita).',
        },
      ),
    ];
  }

  get hayResumen(): boolean {
    return this.filasResumenV.length > 0;
  }

  // El resumen son números, no un gráfico: como card se comía media pantalla
  // para algo que se mira de a ratos (al terminar una corrida, o al comparar dos
  // rangos). Vive detrás del botón del header.
  abrirResumen(): void {
    this.formularios.abrir(ResumenPeriodoDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        filas: this.filasResumenV,
        periodo: this.periodoTexto,
        help: this.helpResumen,
      },
    });
  }

  /**
   * Informe PDF del período: resumen, cupos por gateway, los dos heatmaps y las
   * series de envío y confirmación. Se arma con lo que ya está en pantalla (no
   * dispara consultas nuevas), así que sale exactamente lo que se está viendo.
   */
  async descargarPdf(): Promise<void> {
    this.generandoPdf = true;
    try {
      const c = this.config?.congestion;
      await this.pdf.generar({
        periodo: this.periodoTexto,
        cliente: LoginService.getCliente()?.nombre,
        resumen: this.filasResumenV.map((f) => ({
          label: f.label,
          valor: f.valor,
          destacado: f.destacado,
        })),
        gateways: this.filasGatewayV,
        cupo: this._matrizPdf(this.chCupoHeat.data),
        tasaFallo: this._matrizPdf(this.chTasaFalloHeat.data, '%'),
        barras: this._barrasPdf(),
        series: this._seriesPdf(),
        congestion: c
          ? {
              'Cupo inicial': String(c.cupoInicial),
              'Rango permitido': `${c.cupoMin} – ${c.cupoMax}`,
              'Umbral para subir el cupo': `${c.umbralSubirPct} %`,
              'Umbral para bajar el cupo': `${c.umbralBajarPct} %`,
              'Umbral de protección (cupo a la mitad)': `${c.umbralProteccionPct} %`,
              'Mínimo de muestras por ventana': String(c.minMuestras),
              'Ventana / cooldown': `${c.ventanaSeg} s`,
              'Memoria del cupo aprendido': `${c.memoriaSeg} s`,
              Estado: c.enabled ? 'Activo' : 'Inactivo',
            }
          : undefined,
      });
    } catch (e: any) {
      this.error = e?.message || 'No se pudo generar el informe';
    } finally {
      this.generandoPdf = false;
    }
  }

  /**
   * Gráficos de barras del informe. Son las cuatro distribuciones que se miran
   * al revisar una corrida y que en el dashboard viven repartidas entre tablas
   * y el resumen: cómo cerró cada paso, de dónde salieron los downlinks, en qué
   * puertos falla el ACK y en qué limiter rebotan los envíos.
   */
  private _barrasPdf(): GraficoPdf[] {
    const C = this.COL;
    const graficos: GraficoPdf[] = [];

    // 1. Veredicto del paso: dónde se cierra el lazo (y cuánto sin verificar).
    const vias: [string, string][] = [
      ['ack', 'ACK (fast-path)'],
      ['ack-no-aplicado', 'ACK sin aplicar'],
      ['rechazado', 'Rechazado por el device'],
      ['eco', 'Eco verificado'],
      ['timeout', 'Timeout (forzado)'],
      ['descartado', 'Descartado'],
      ['abortado', 'Plan abortado'],
    ];
    graficos.push({
      titulo: 'Cómo cerró cada paso del plan',
      tipo: 'barras',
      categorias: vias.map(([, label]) => label),
      series: [
        {
          nombre: 'Pasos',
          color: C.violet,
          datos: vias.map(([via]) =>
            this.sumSample('seq_pasos_veredicto_total', { via }),
          ),
        },
      ],
    });

    // 2. Downlinks por origen: enviados vs descartados.
    const filas = this.filasOrigenV;
    if (filas.length) {
      graficos.push({
        titulo: 'Downlinks por origen',
        tipo: 'barras',
        categorias: filas.map((f) => f.label),
        series: [
          {
            nombre: 'Enviados',
            color: C.blue,
            datos: filas.map((f) =>
              this.sumSample('downlinks_sent_total', f.match),
            ),
          },
          {
            nombre: 'Descartados',
            color: C.amber,
            datos: filas.map((f) =>
              this.sumSample('downlinks_descartados_total', f.match),
            ),
          },
          {
            nombre: 'Fallidos',
            color: C.red,
            datos: filas.map((f) =>
              this.sumSample('downlinks_failed_total', f.match),
            ),
          },
        ],
      });
    }

    // 3. ACK por puerto: los puertos con más actividad. Es el corte con el que
    // se detecta que un aspecto puntual de la configuración no entra nunca.
    const puertos = new Map<string, { ok: number; falso: number }>();
    for (const [metrica, campo] of [
      ['seq_ack_ok_total', 'ok'],
      ['seq_ack_timeout_total', 'falso'],
    ] as [string, 'ok' | 'falso'][]) {
      for (const x of this.getSamplesByLabel(metrica, 'puerto')) {
        const e = puertos.get(x.label) ?? { ok: 0, falso: 0 };
        e[campo] += x.value;
        puertos.set(x.label, e);
      }
    }
    const topPuertos = [...puertos.entries()]
      .sort((a, b) => b[1].ok + b[1].falso - (a[1].ok + a[1].falso))
      .slice(0, 12)
      // Orden natural por número de puerto para que el eje se lea.
      .sort((a, b) => Number(a[0]) - Number(b[0]));
    if (topPuertos.length) {
      graficos.push({
        titulo: 'ACK por puerto (los 12 puertos con más envíos)',
        tipo: 'barras',
        categorias: topPuertos.map(([p]) => `pto ${p}`),
        series: [
          {
            nombre: 'ACK true',
            color: C.green,
            datos: topPuertos.map(([, v]) => v.ok),
          },
          {
            nombre: 'ACK false',
            color: C.red,
            datos: topPuertos.map(([, v]) => v.falso),
          },
        ],
      });
    }

    // 4. Rebotes de rate limit por limiter.
    const rl = this.rateLimitItemsV;
    if (rl.length) {
      graficos.push({
        titulo: 'Rebotes de rate limit por limiter',
        tipo: 'barras',
        categorias: rl.map((x) => x.nombre ?? x.identificador ?? x.titulo),
        series: [
          { nombre: 'Rebotes', color: C.amber, datos: rl.map((x) => x.value) },
        ],
      });
    }

    return graficos;
  }

  // Series temporales del informe: las mismas que el dashboard grafica por
  // franja.
  private _seriesPdf(): GraficoPdf[] {
    const serie = (x: { name: string; data: unknown; color?: string }) => ({
      nombre: x.name,
      color: x.color ?? this.COL.gray,
      datos: (x.data as number[]) ?? [],
    });
    return [
      {
        titulo: 'Envíos por franja',
        tipo: 'lineas',
        categorias: this.catFranjas,
        series: this.chSent.map(serie),
      },
      {
        titulo: 'Confirmación por franja',
        tipo: 'lineas',
        categorias: this.catFranjas,
        series: this.chConfirm.map(serie),
      },
      {
        titulo: 'ACK false sobre el total de SET resueltos (%)',
        tipo: 'lineas',
        categorias: this.catFranjas,
        series: this.chAckRatio.map(serie),
        unidad: '%',
      },
    ];
  }

  // Las celdas del heatmap vienen como [x, y, valor]; el informe las quiere como
  // matriz [fila][columna].
  private _matrizPdf(
    celdas: [number, number, number][],
    unidad?: string,
  ): MatrizPdf | undefined {
    if (!this.gwHeatNombres.length || !this.catFranjas.length) return undefined;
    const m = this.gwHeatNombres.map(() => this.catFranjas.map(() => 0));
    for (const [x, y, v] of celdas) {
      if (m[y] && x < m[y].length) m[y][x] = v;
    }
    return {
      celdas: m,
      filas: this.gwHeatNombres,
      columnas: this.catFranjas,
      unidad,
    };
  }

  // Rango elegido, en texto. El modal tapa el filtro, así que lo repite, y el
  // informe lo pone en su encabezado.
  //
  // Formato explícito dd/mm/aaaa hh:mm y NO `_fmtHora` (que es el de los ejes
  // de los gráficos): ahí alcanza con "03/09 14:00", pero en un informe queda
  // "3/9, 10:44 a. m." — sin año, con coma y en 12 horas.
  get periodoTexto(): string {
    const { start, end } = this.rangoPeriodo ?? {};
    if (!start || !end) return '';
    return `${this._fmtFechaHora(start)} → ${this._fmtFechaHora(end)}`;
  }

  private _fmtFechaHora(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return (
      `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ` +
      `${p(d.getHours())}:${p(d.getMinutes())}`
    );
  }

  private _rangoPeriodoDefault(): DateRangeValue {
    const fin = new Date();
    const ini = new Date(fin.getTime() - 24 * 3600_000);
    return { start: ini.toISOString(), end: fin.toISOString() };
  }

  // Trae las franjas persistidas del período (o mock para previsualizar).
  private async _fetchDatosPeriodo(): Promise<
    IResumenDatosBase<'Downlinks Métricas Sistema'>[]
  > {
    if (this.USAR_MOCK_HISTORICO) return this._mockDatosPeriodo();
    if (!this.rangoPeriodo?.start || !this.rangoPeriodo?.end) return [];
    const filter: IFilter<IResumenDatos> = {
      tipo: 'Downlinks Métricas Sistema',
      periodoInicio: {
        $gte: this.rangoPeriodo.start,
        $lte: this.rangoPeriodo.end,
      },
    } as unknown as IFilter<IResumenDatos>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      includeChildren: true,
      limit: 100000,
    };
    const persistidos = (
      await this.resumenesDatosService.getFiltered<'Downlinks Métricas Sistema'>(
        query,
      )
    ).datos;
    return this._mergeFranjaActual(persistidos);
  }

  // Mergea la franja horaria EN CURSO (Redis en vivo) sobre las persistidas, si
  // el período llega hasta ahora → tiempo real (0 lag). Reemplaza la franja de
  // la hora actual (que puede tener un upsert parcial) por la versión viva.
  private async _mergeFranjaActual(
    persistidos: IResumenDatosBase<'Downlinks Métricas Sistema'>[],
  ): Promise<IResumenDatosBase<'Downlinks Métricas Sistema'>[]> {
    try {
      const franja = await this.metricasService.getFranjaActual();
      const inicio = franja?.periodoInicio;
      if (!inicio || !franja?.sistema?.length) return persistidos;
      // Si el período elegido termina antes de la hora en curso, no aplica.
      if (this.rangoPeriodo?.end && this.rangoPeriodo.end < inicio)
        return persistidos;
      const sinActual = persistidos.filter((d) => d.periodoInicio !== inicio);
      const vivos = franja.sistema.map(
        (s) =>
          ({
            tipo: 'Downlinks Métricas Sistema',
            agrupacion: 'Cliente',
            agrupacionTiempo: 'Horario',
            periodoInicio: inicio,
            resumen: s.resumen,
          }) as unknown as IResumenDatosBase<'Downlinks Métricas Sistema'>,
      );
      return [...sinActual, ...vivos];
    } catch {
      return persistidos;
    }
  }

  // Construye las series por franja horaria para los gráficos de cada sección.
  private _construirCharts(
    datos: IResumenDatosBase<'Downlinks Métricas Sistema'>[],
  ): void {
    const horas = [
      ...new Set(
        datos.map((d) => d.periodoInicio).filter((h): h is string => !!h),
      ),
    ].sort();
    this.catFranjas = horas.map((h) => this._fmtHora(h));
    const idx = new Map(horas.map((h, i) => [h, i]));

    // Suma por franja de una métrica (opcionalmente filtrando por labels).
    const serie = (
      metrica: string,
      filt?: Record<string, string>,
    ): number[] => {
      const arr = horas.map(() => 0);
      for (const d of datos) {
        const i = d.periodoInicio ? idx.get(d.periodoInicio) : undefined;
        if (i == null) continue;
        for (const s of d.resumen?.samples ?? []) {
          if (s.metrica !== metrica) continue;
          if (
            filt &&
            !Object.entries(filt).every(([k, v]) => s.labels?.[k] === v)
          )
            continue;
          arr[i] += s.valor ?? 0;
        }
      }
      return arr;
    };

    const C = this.COL;
    this.chSent = [
      { name: 'Enviados', data: serie('downlinks_sent_total'), color: C.blue },
      {
        name: 'Descartados',
        data: serie('downlinks_descartados_total'),
        color: C.amber,
      },
      { name: 'Fallidos', data: serie('downlinks_failed_total'), color: C.red },
    ];
    const ackOk = serie('seq_ack_ok_total');
    const ackFalse = serie('seq_ack_timeout_total');
    this.chConfirm = [
      { name: 'ACK true', data: ackOk, color: C.green },
      { name: 'ACK false', data: ackFalse, color: C.red },
      {
        name: 'Uplink ok',
        data: serie('seq_autoget_eco_ok_total'),
        color: C.teal,
      },
      {
        name: 'Uplink timeout',
        data: serie('seq_autoget_eco_timeout_total'),
        color: C.amber,
      },
    ];
    this.chAckRatio = [
      {
        name: 'ACK false %',
        color: C.red,
        data: ackOk.map((ok, i) => {
          const t = ok + ackFalse[i];
          return t ? Math.round((ackFalse[i] / t) * 100) : 0;
        }),
      },
    ];
    // Cola: pico de tamaño por franja.
    const cola = horas.map(() => 0);
    for (const d of datos) {
      const i = d.periodoInicio ? idx.get(d.periodoInicio) : undefined;
      if (i != null && d.resumen?.colaProfundidadMax != null)
        cola[i] = Math.max(cola[i], d.resumen.colaProfundidadMax);
    }
    this.chCola = [{ name: 'Tamaño máx', data: cola, color: C.teal }];
    this.chSeq = [
      {
        name: 'Planes creados',
        data: serie('seq_planes_creados_total'),
        color: C.blue,
      },
      {
        name: 'Completados',
        data: serie('seq_planes_completados_total'),
        color: C.green,
      },
      {
        name: 'Abortados',
        data: serie('seq_planes_abortados_total'),
        color: C.red,
      },
      {
        name: 'Pasos forzados',
        data: serie('seq_pasos_forzados_total'),
        color: C.amber,
      },
    ];
    this.chRecon = [
      {
        name: 'Divergentes',
        data: serie('reconciliation_diffs_found_total'),
        color: C.amber,
      },
      {
        name: 'Bloqueadas',
        data: serie('reconciliation_blocked_devices_total'),
        color: C.red,
      },
      {
        name: 'Offline omitidos',
        data: serie('reconciliation_skipped_offline_total'),
        color: C.gray,
      },
      {
        name: 'Nocturna omitidos',
        data: serie('reconciliation_skipped_nocturna_total'),
        color: C.violet,
      },
      {
        // Gauge point-in-time (último valor de la franja): configs en churn.
        name: 'En churn',
        data: serie('reconciliation_configs_churn'),
        color: C.teal,
      },
    ];
    this.chConsulta = [
      {
        name: 'Consultados',
        data: serie('consulta_config_consultados_total'),
        color: C.blue,
      },
      {
        name: 'Bloqueados',
        data: serie('consulta_config_bloqueados_total'),
        color: C.red,
      },
      {
        name: 'Offline',
        data: serie('consulta_config_offline_total'),
        color: C.gray,
      },
      {
        // Gauge point-in-time: pendientes (config incompleta).
        name: 'Pendientes',
        data: serie('consulta_config_pendientes'),
        color: C.violet,
      },
    ];
    this.chReintentos = [
      {
        name: 'Reencolados',
        data: serie('reintentos_descartados_total'),
        color: C.amber,
      },
    ];
    this.chBarrido = [
      {
        name: 'No ejecutado',
        data: serie('comandos_barridos_no_ejecutado_total'),
        color: C.red,
      },
    ];
    // Congestión por gateway en el tiempo, como heatmap (X = franja, Y =
    // gateway). El cupo TIENE que estabilizarse: si oscila, la banda muerta es
    // angosta o la ventana es corta, y cada bajada difiere planes. Con muchos
    // gateways el patrón se lee de un vistazo y una línea por cada uno no.
    const gwsSerie = [
      ...new Set(
        datos.flatMap((d) =>
          (d.resumen?.samples ?? [])
            .filter(
              (x) =>
                x.metrica === 'dlk_cc_cupo' ||
                x.metrica === 'dlk_cc_tasa_fallo',
            )
            .map((x) => x.labels?.['gw'] ?? ''),
        ),
      ),
    ].filter(Boolean);
    this.gwHeatCats = gwsSerie.map((gw) => this.scopeLabelHtml(`gw:${gw}`));
    this.gwHeatNombres = gwsSerie.map((gw) => this.scopeLabel(`gw:${gw}`));
    const cupoCeldas: [number, number, number][] = [];
    const tasaCeldas: [number, number, number][] = [];
    gwsSerie.forEach((gw, y) => {
      // Un gauge no se suma entre franjas: `serie()` acumula, así que para
      // estas dos se toma el valor de la franja (hay uno por franja).
      const cupo = serie('dlk_cc_cupo', { gw });
      const tasa = serie('dlk_cc_tasa_fallo', { gw });
      horas.forEach((_, x) => {
        cupoCeldas.push([x, y, cupo[x]]);
        // La tasa se persiste como fracción; el heatmap va en %.
        tasaCeldas.push([x, y, Math.round(tasa[x] * 100)]);
      });
    });
    this.chCupoHeat = { data: cupoCeldas, name: 'Cupo' };
    this.chTasaFalloHeat = { data: tasaCeldas, name: 'Tasa de fallo %' };

    // Heatmap rate-limit: tiempo × scope (limiter/gateway).
    const scopes = [
      ...new Set(
        datos.flatMap((d) =>
          (d.resumen?.samples ?? [])
            .filter((s) => s.metrica === 'downlinks_rate_limited_total')
            .map((s) => s.labels?.['scope'] ?? 'global'),
        ),
      ),
    ];
    this.rlScopes = scopes;
    const data: [number, number, number][] = [];
    horas.forEach((h, x) => {
      const i = idx.get(h)!;
      scopes.forEach((sc, y) => {
        data.push([
          x,
          y,
          serie('downlinks_rate_limited_total', { scope: sc })[i],
        ]);
      });
    });
    this.chRlHeatmap = { data, name: 'Rebotes' };
  }

  private _fmtHora(iso: string): string {
    return new Date(iso)
      .toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', '');
  }

  // Datos de ejemplo (franjas) para previsualizar el modo período.
  private _mockDatosPeriodo(): IResumenDatosBase<'Downlinks Métricas Sistema'>[] {
    const horas = [
      '2026-07-27T15:00:00.000Z',
      '2026-07-27T16:00:00.000Z',
      '2026-07-27T17:00:00.000Z',
      '2026-07-27T18:00:00.000Z',
      '2026-07-27T19:00:00.000Z',
    ];
    const sent = [120, 85, 60, 40, 30];
    const ackOk = [70, 40, 25, 30, 26];
    const ackF = [30, 30, 27, 4, 1];
    const ecoTo = [12, 18, 20, 2, 0];
    const rlGlobal = [12, 20, 18, 4, 1];
    const rlGw = [3, 8, 15, 2, 0];
    const div = [8, 6, 5, 2, 1];
    const cola = [14, 22, 19, 6, 3];
    return horas.map((h, i) => ({
      tipo: 'Downlinks Métricas Sistema',
      agrupacion: 'Cliente',
      agrupacionTiempo: 'Horario',
      periodoInicio: h,
      resumen: {
        colaProfundidadMax: cola[i],
        colaProfundidadProm: Math.round(cola[i] / 2),
        samples: [
          {
            metrica: 'downlinks_sent_total',
            labels: { origen: 'Reconciliacion' },
            valor: sent[i],
          },
          {
            metrica: 'downlinks_descartados_total',
            labels: { origen: 'Reconciliacion' },
            valor: Math.round(sent[i] / 20),
          },
          {
            metrica: 'downlinks_failed_total',
            labels: { origen: 'Reconciliacion' },
            valor: i === 0 ? 2 : 0,
          },
          {
            metrica: 'seq_ack_ok_total',
            labels: { puerto: '12' },
            valor: ackOk[i],
          },
          {
            metrica: 'seq_ack_timeout_total',
            labels: { puerto: '12' },
            valor: ackF[i],
          },
          {
            metrica: 'seq_autoget_eco_ok_total',
            labels: { puertoGet: '15' },
            valor: Math.round(ackOk[i] / 3),
          },
          {
            metrica: 'seq_autoget_eco_timeout_total',
            labels: { puertoGet: '15' },
            valor: ecoTo[i],
          },
          {
            metrica: 'seq_planes_creados_total',
            labels: { origen: 'Reconciliacion' },
            valor: 18 - i,
          },
          {
            metrica: 'seq_planes_completados_total',
            labels: { origen: 'Reconciliacion' },
            valor: 14 - i,
          },
          {
            metrica: 'seq_pasos_forzados_total',
            labels: { origen: 'Reconciliacion' },
            valor: ackF[i],
          },
          {
            metrica: 'seq_planes_abortados_total',
            labels: { motivo: 'offline' },
            valor: [1, 2, 3, 0, 0, 0][i],
          },
          {
            metrica: 'downlinks_rate_limited_total',
            labels: { scope: 'global' },
            valor: rlGlobal[i],
          },
          {
            metrica: 'downlinks_rate_limited_total',
            labels: { scope: 'gw:0000000011223344' },
            valor: rlGw[i],
          },
          { metrica: 'reconciliation_runs_total', labels: {}, valor: 12 },
          {
            metrica: 'reconciliation_diffs_found_total',
            labels: {},
            valor: div[i],
          },
          {
            metrica: 'reconciliation_skipped_offline_total',
            labels: {},
            valor: 3 - Math.min(3, i),
          },
          {
            metrica: 'reconciliation_skipped_nocturna_total',
            labels: {},
            valor: [4, 3, 2, 1, 0][i],
          },
          {
            metrica: 'reconciliation_blocked_devices_total',
            labels: {},
            valor: [0, 0, 1, 0, 0, 0][i],
          },
          {
            metrica: 'consulta_config_consultados_total',
            labels: {},
            valor: 5 - Math.min(5, i),
          },
          {
            metrica: 'consulta_config_bloqueados_total',
            labels: {},
            valor: [0, 1, 0, 0, 0, 0][i],
          },
          {
            metrica: 'consulta_config_offline_total',
            labels: {},
            valor: [3, 2, 2, 1, 1, 0][i],
          },
          {
            metrica: 'seq_confirm_latencia_seg',
            labels: { le: '15' },
            valor: ackOk[i],
          },
          {
            metrica: 'seq_confirm_latencia_seg',
            labels: { le: '30' },
            valor: Math.round(ackF[i] / 2),
          },
          {
            metrica: 'seq_confirm_latencia_seg',
            labels: { le: '60' },
            valor: Math.round(ackF[i] / 3),
          },
          {
            metrica: 'seq_confirm_latencia_seg',
            labels: { le: '120' },
            valor: i < 3 ? 4 : 0,
          },
          {
            metrica: 'seq_confirm_latencia_seg',
            labels: { le: '+Inf' },
            valor: i < 2 ? 2 : 0,
          },
          {
            metrica: 'downlinks_rate_limited_luminarias',
            labels: { scope: 'global' },
            valor: [4, 9, 11, 2, 1, 0][i],
          },
          {
            metrica: 'downlinks_rate_limited_luminarias',
            labels: { scope: 'gw:0000000011223344' },
            valor: [1, 3, 6, 1, 0, 0][i],
          },
          // Tiempo-a-convergencia (histograma le): más lentas al principio.
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '60' },
            valor: [0, 1, 2, 3, 4][i],
          },
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '300' },
            valor: [1, 2, 3, 2, 1][i],
          },
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '900' },
            valor: [2, 3, 2, 1, 0][i],
          },
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '3600' },
            valor: [3, 2, 1, 0, 0][i],
          },
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '21600' },
            valor: [2, 1, 0, 0, 0][i],
          },
          {
            metrica: 'reconciliation_convergencia_seg',
            labels: { le: '+Inf' },
            valor: [1, 1, 0, 0, 0][i],
          },
          // Gauges point-in-time (bajan a medida que convergen las deseadas).
          {
            metrica: 'reconciliation_configs_churn',
            labels: {},
            valor: [9, 7, 4, 2, 1][i],
          },
          {
            metrica: 'consulta_config_pendientes',
            labels: {},
            valor: [14, 11, 8, 5, 3][i],
          },
          // Cola BullMQ por estado (tiles de la card "Estado de la Cola").
          {
            metrica: 'downlinks_queue_size',
            labels: { state: 'waiting' },
            valor: [22, 18, 12, 6, 3][i],
          },
          {
            metrica: 'downlinks_queue_size',
            labels: { state: 'delayed' },
            valor: [7, 5, 3, 1, 0][i],
          },
          {
            metrica: 'downlinks_queue_size',
            labels: { state: 'active' },
            valor: [3, 2, 2, 1, 1][i],
          },
          {
            metrica: 'downlinks_queue_size',
            labels: { state: 'completed' },
            valor: [80, 60, 45, 30, 20][i],
          },
          {
            metrica: 'downlinks_queue_size',
            labels: { state: 'failed' },
            valor: [2, 1, 0, 0, 0][i],
          },
          // Secuenciador: resto de contadores de los tiles.
          {
            metrica: 'seq_planes_saltados_total',
            labels: {},
            valor: [3, 2, 2, 1, 1][i],
          },
          {
            metrica: 'seq_pasos_reintentados_total',
            labels: {},
            valor: [6, 8, 7, 3, 1][i],
          },
          {
            metrica: 'seq_add_fallido_total',
            labels: {},
            valor: [0, 1, 0, 0, 0][i],
          },
          {
            metrica: 'seq_planes_reemplazados_version_total',
            labels: {},
            valor: [1, 2, 1, 0, 0][i],
          },
          // Reintentos de descartados + barrido.
          {
            metrica: 'reintentos_descartados_total',
            labels: {},
            valor: [2, 3, 1, 0, 0][i],
          },
          {
            metrica: 'comandos_barridos_no_ejecutado_total',
            labels: {},
            valor: [0, 1, 1, 0, 0][i],
          },
          // Envíos por Origen: Manual (por nivel) / AutoGet / ConsultaConfig,
          // para que la tabla no muestre solo Reconciliación.
          {
            metrica: 'downlinks_sent_total',
            labels: { origen: 'Manual', objetivo: 'luminaria' },
            valor: [8, 6, 4, 3, 2][i],
          },
          {
            metrica: 'downlinks_sent_total',
            labels: { origen: 'Manual', objetivo: 'grupo' },
            valor: [5, 4, 3, 2, 1][i],
          },
          {
            metrica: 'downlinks_sent_total',
            labels: { origen: 'AutoGet' },
            valor: [20, 15, 12, 8, 5][i],
          },
          {
            metrica: 'downlinks_sent_total',
            labels: { origen: 'ConsultaConfig' },
            valor: [10, 8, 6, 4, 2][i],
          },
          {
            metrica: 'downlinks_descartados_total',
            labels: { origen: 'Manual', objetivo: 'luminaria' },
            valor: [1, 0, 1, 0, 0][i],
          },
        ],
      },
    })) as unknown as IResumenDatosBase<'Downlinks Métricas Sistema'>[];
  }
}

// ── MOCK DATA ────────────────────────────────────────────────────────────────
// Descomentar `this.estado = MOCK_ESTADO` en refresh() para usar estos datos.
// const MOCK_ESTADO: MetricasEstado = {
//   metricas: {
//     downlinks_queue_size: {
//       type: 'gauge',
//       help: 'Tamaño actual de la queue BullMQ de downlinks',
//       samples: [
//         { labels: { state: 'waiting' }, value: 12 },
//         { labels: { state: 'delayed' }, value: 5 },
//         { labels: { state: 'active' }, value: 3 },
//         { labels: { state: 'completed' }, value: 847 },
//         { labels: { state: 'failed' }, value: 2 },
//       ],
//     },
//     downlinks_sent_total: {
//       type: 'counter',
//       help: 'Downlinks enviados a Chirpstack',
//       samples: [
//         { labels: { origen: 'Reconciliacion' }, value: 512 },
//         { labels: { origen: 'Manual', objetivo: 'luminaria' }, value: 87 },
//         { labels: { origen: 'Manual', objetivo: 'grupo' }, value: 134 },
//         { labels: { origen: 'Manual', objetivo: 'puesta' }, value: 23 },
//         { labels: { origen: 'Manual', objetivo: 'grupoPuesta' }, value: 41 },
//         { labels: { origen: 'AutoGet' }, value: 201 },
//       ],
//     },
//     downlinks_descartados_total: {
//       type: 'counter',
//       help: 'Downlinks descartados',
//       samples: [
//         { labels: { origen: 'Reconciliacion' }, value: 23 },
//         { labels: { origen: 'Manual', objetivo: 'luminaria' }, value: 4 },
//         { labels: { origen: 'Manual', objetivo: 'grupo' }, value: 7 },
//         { labels: { origen: 'Manual', objetivo: 'puesta' }, value: 2 },
//         { labels: { origen: 'Manual', objetivo: 'grupoPuesta' }, value: 3 },
//         { labels: { origen: 'AutoGet' }, value: 0 },
//       ],
//     },
//     downlinks_failed_total: {
//       type: 'counter',
//       help: 'Downlinks que terminaron en Fallido',
//       samples: [
//         { labels: { origen: 'Reconciliacion' }, value: 2 },
//         { labels: { origen: 'Manual', objetivo: 'luminaria' }, value: 0 },
//         { labels: { origen: 'Manual', objetivo: 'grupo' }, value: 1 },
//         { labels: { origen: 'Manual', objetivo: 'puesta' }, value: 0 },
//         { labels: { origen: 'Manual', objetivo: 'grupoPuesta' }, value: 1 },
//         { labels: { origen: 'AutoGet' }, value: 0 },
//       ],
//     },
//     downlinks_rate_limited_total: {
//       type: 'counter',
//       help: 'Veces que un job rebotó en un rate-limiter',
//       samples: [
//         { labels: { scope: 'global' }, value: 45 },
//         { labels: { scope: 'gw:0000000011223344' }, value: 18 },
//         { labels: { scope: 'nogw' }, value: 3 },
//       ],
//     },
//     reconciliation_runs_total: {
//       type: 'counter',
//       help: 'Corridas del cron reconciliador',
//       samples: [{ labels: {}, value: 96 }],
//     },
//     reconciliation_diffs_found_total: {
//       type: 'counter',
//       help: 'Configuraciones divergentes detectadas',
//       samples: [{ labels: {}, value: 34 }],
//     },
//     reconciliation_blocked_devices_total: {
//       type: 'counter',
//       help: 'Dispositivos puestos en bloqueoHasta',
//       samples: [{ labels: {}, value: 3 }],
//     },
//     reintentos_descartados_total: {
//       type: 'counter',
//       help: 'Comandos manuales reencolados por el cron de reintentos',
//       samples: [{ labels: {}, value: 11 }],
//     },
//   },
//   config: {
//     orquestador: {
//       queueName: 'downlinks-luminarias',
//       concurrency: 8,
//       rateGlobalPerSec: 20,
//       ratePerGwMs: 300,
//       rateNoGwSec: 1,
//       expiresMinManual: 5,
//       expiresMinReconciliacion: 15,
//     },
//     secuenciador: {
//       deadlineAckSec: 15,
//       deadlineEcoSec: 30,
//       maxReintentosPaso: 2,
//       planTtlSec: 1800,
//       sendBufferSec: 180,
//     },
//     reconciliador: {
//       enabled: true,
//       cron: '0 */15 * * * *',
//       batchSize: 500,
//       backoffIntentos: 5,
//       bloqueoHoras: 6,
//     },
//     reintentosDescartados: {
//       enabled: true,
//       cron: '*/5 * * * *',
//       ventanaMin: 30,
//     },
//   },
// };
